import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { SearchIcon, RepeatIcon, CopyIcon } from "@chakra-ui/icons";
import { doc, getDoc } from "firebase/firestore";
import { CustomDatePicker } from "../../CustomReusables/CustomDatePicker"; // adjust path
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import GlobalSpinner from "../../CustomReusables/GlobalSpinner";
import { db } from "../../../configs/firebase";
import { useAuth } from "../../../auth/AuthContext";

// =======================
// CONFIG
// =======================
const API_TRAVEL =
  process.env.REACT_APP_BACKEND_URL + "/api/v1/travel/accommodations";
const BOOKINGS_ENDPOINT = `${API_TRAVEL}/accommodation-bookings`;

const cardsBorderRadii = "8px";

// =======================
// TYPES
// =======================
type BookingStatus =
  | "confirmed"
  | "cancelled"
  | "pending"
  | "checked_in"
  | "checked_out";

type AccommodationBooking = {
  _id: string;
  accommodationId: string;
  roomId: string;
  userId?: string; // customer / guest who made the booking
  ownerUserId?: string; // accommodation owner
  ownerId?: string;
  sellerUserId?: string;
  createdBy?: string;
  orderId?: string;
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD (exclusive)
  roomsBooked?: number;
  guests?: number;
  status: BookingStatus;
  createdAt?: string;
  updatedAt?: string;

  // optional if you add them later
  checkedInAt?: string;
  checkedOutAt?: string;
};

type BookingUserProfile = {
  displayName?: string;
  name?: string;
  businessName?: string;
  email?: string;
};

type AccommodationRecord = {
  _id?: string;
  id?: string;
  userId?: string;
  ownerId?: string;
  ownerUserId?: string;
  sellerUserId?: string;
  createdBy?: string;
  ownerEmail?: string;
};

const cleanAccessValue = (value: unknown) => String(value || "").trim();

const normalizeAccessRole = (value: unknown) =>
  cleanAccessValue(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const extractResponseArray = <T,>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.bookings)) return payload.bookings;
  if (Array.isArray(payload?.accommodations)) return payload.accommodations;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.bookings)) return payload.data.bookings;
  if (Array.isArray(payload?.data?.accommodations)) {
    return payload.data.accommodations;
  }

  return [];
};

const getAccommodationRecordId = (accommodation: AccommodationRecord) =>
  cleanAccessValue(accommodation?._id || accommodation?.id);

const getAccommodationOwnerId = (
  accommodation: AccommodationRecord,
  currentUserUid = "",
  currentUserEmail = "",
) => {
  const ownerId = cleanAccessValue(
    accommodation?.ownerUserId ||
      accommodation?.ownerId ||
      accommodation?.sellerUserId ||
      accommodation?.createdBy ||
      accommodation?.userId,
  );

  if (ownerId) return ownerId;

  const savedOwnerEmail = cleanAccessValue(
    accommodation?.ownerEmail,
  ).toLowerCase();
  const viewerEmail = cleanAccessValue(currentUserEmail).toLowerCase();

  if (savedOwnerEmail && viewerEmail && savedOwnerEmail === viewerEmail) {
    return cleanAccessValue(currentUserUid);
  }

  return "";
};

const getBookingDirectOwnerId = (booking: AccommodationBooking) =>
  cleanAccessValue(
    booking?.ownerUserId ||
      booking?.ownerId ||
      booking?.sellerUserId ||
      booking?.createdBy,
  );

function getResolvedUserName(
  profile: BookingUserProfile | null,
  userId?: string,
) {
  const normalizedUserId = String(userId || "")
    .trim()
    .toLowerCase();

  if (!normalizedUserId || normalizedUserId === "guest") {
    return "Guest customer";
  }

  if (!profile) {
    return "Customer unavailable";
  }

  return String(
    profile.displayName ||
      profile.name ||
      profile.businessName ||
      profile.email ||
      "Customer",
  ).trim();
}

function toISO(d: Date) {
  return d.toISOString().slice(0, 10);
}

function isToday(iso: string) {
  const today = new Date();
  const t = today.toISOString().slice(0, 10);
  return iso === t;
}

function statusColor(status: BookingStatus) {
  switch (status) {
    case "confirmed":
      return "green";
    case "pending":
      return "yellow";
    case "cancelled":
      return "red";
    case "checked_in":
      return "blue";
    case "checked_out":
      return "purple";
    default:
      return "gray";
  }
}

function formatDateTime(dt?: string) {
  if (!dt) return "—";
  const d = new Date(dt);
  return isNaN(+d) ? "—" : d.toLocaleString();
}

function nightsBetween(checkIn: string, checkOut: string) {
  const a = new Date(checkIn);
  const b = new Date(checkOut);
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  const diff = Math.ceil((+b - +a) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

export default function AdminAccommodationBookings() {
  const { theme } = useCustomTheme();
  const { user } = useAuth();
  const toast = useToast();
  const details = useDisclosure();

  const [currentUserRole, setCurrentUserRole] = useState("");
  const [accessLoading, setAccessLoading] = useState(true);
  const [accommodationOwnersById, setAccommodationOwnersById] = useState<
    Record<string, string>
  >({});

  const currentUserUid = cleanAccessValue(user?.uid);
  const currentUserEmail = cleanAccessValue(user?.email);
  const isSuperAdmin = normalizeAccessRole(currentUserRole) === "superadmin";

  const [bookings, setBookings] = useState<AccommodationBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [userNamesById, setUserNamesById] = useState<Record<string, string>>(
    {},
  );

  // Filters
  const [status, setStatus] = useState<BookingStatus | "all">("all");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [accommodationId, setAccommodationId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [userId, setUserId] = useState("");
  const [q, setQ] = useState("");

  // Sorting
  const [sortKey, setSortKey] = useState<"createdAt" | "checkIn" | "checkOut">(
    "createdAt",
  );
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");

  // Selected booking
  const [selected, setSelected] = useState<AccommodationBooking | null>(null);

  // Action loading states
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      if (!user?.uid) {
        if (!cancelled) {
          setCurrentUserRole("");
          setBookings([]);
          setAccommodationOwnersById({});
          setAccessLoading(false);
        }
        return;
      }

      setAccessLoading(true);

      try {
        const userSnapshot = await getDoc(doc(db, "users", user.uid));
        const userData = userSnapshot.exists() ? userSnapshot.data() : {};

        const resolvedRole =
          userData?.role ||
          userData?.userRole ||
          (user as any)?.role ||
          (user as any)?.userRole ||
          (user as any)?.claims?.role ||
          "";

        if (!cancelled) {
          setCurrentUserRole(String(resolvedRole));
        }
      } catch (error) {
        console.error("Failed to load accommodation admin access:", error);

        if (!cancelled) {
          // Safe fallback: a role lookup failure never grants platform access.
          setCurrentUserRole("");
        }
      } finally {
        if (!cancelled) {
          setAccessLoading(false);
        }
      }
    };

    void loadAccess();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const fetchBookings = useCallback(async () => {
    if (accessLoading) return;

    if (!currentUserUid) {
      setBookings([]);
      setAccommodationOwnersById({});
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params: any = {};
      if (status !== "all") params.status = status;
      if (fromDate) params.from = toISO(fromDate);
      if (toDate) params.to = toISO(toDate);
      if (accommodationId.trim()) {
        params.accommodationId = accommodationId.trim();
      }
      if (q.trim()) params.q = q.trim();

      /*
       * The backend should derive this scope from the verified Firebase token.
       * These hints help compatible endpoints, while the client also applies
       * a strict ownership filter to every returned booking.
       */
      if (!isSuperAdmin) {
        params.ownerUserId = currentUserUid;
        params.accountUserId = currentUserUid;
      }

      const [bookingsResult, accommodationsResult] = await Promise.allSettled([
        axios.get(BOOKINGS_ENDPOINT, { params }),
        axios.get(API_TRAVEL),
      ]);

      if (bookingsResult.status !== "fulfilled") {
        throw bookingsResult.reason;
      }

      const allBookings = extractResponseArray<AccommodationBooking>(
        bookingsResult.value.data,
      );

      let ownerMap: Record<string, string> = {};

      if (accommodationsResult.status === "fulfilled") {
        const accommodations = extractResponseArray<AccommodationRecord>(
          accommodationsResult.value.data,
        );

        ownerMap = accommodations.reduce<Record<string, string>>(
          (map, accommodation) => {
            const accommodationRecordId =
              getAccommodationRecordId(accommodation);
            const ownerId = getAccommodationOwnerId(
              accommodation,
              currentUserUid,
              currentUserEmail,
            );

            if (accommodationRecordId && ownerId) {
              map[accommodationRecordId] = ownerId;
            }

            return map;
          },
          {},
        );
      } else {
        console.warn(
          "Failed to load accommodations for booking ownership:",
          accommodationsResult.reason,
        );
      }

      setAccommodationOwnersById(ownerMap);

      const accountBookings = isSuperAdmin
        ? allBookings
        : allBookings.filter((booking) => {
            const directOwnerId = getBookingDirectOwnerId(booking);
            const accommodationOwnerId =
              ownerMap[cleanAccessValue(booking.accommodationId)] || "";

            return (
              directOwnerId === currentUserUid ||
              accommodationOwnerId === currentUserUid
            );
          });

      setBookings(accountBookings);
    } catch (e: any) {
      console.error(e);
      setBookings([]);
      setAccommodationOwnersById({});
      toast({
        title: "Failed to load bookings",
        description:
          e?.response?.data?.message || "Check backend route / permissions.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [
    accessLoading,
    accommodationId,
    currentUserEmail,
    currentUserUid,
    fromDate,
    isSuperAdmin,
    q,
    status,
    toDate,
    toast,
  ]);

  useEffect(() => {
    if (accessLoading) return;

    void fetchBookings();
  }, [accessLoading, currentUserUid, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const getBookingOwnerId = useCallback(
    (booking: AccommodationBooking) => {
      return (
        getBookingDirectOwnerId(booking) ||
        accommodationOwnersById[cleanAccessValue(booking.accommodationId)] ||
        ""
      );
    },
    [accommodationOwnersById],
  );

  const canManageBooking = useCallback(
    (booking: AccommodationBooking) => {
      if (isSuperAdmin) return true;
      if (!currentUserUid) return false;

      return getBookingOwnerId(booking) === currentUserUid;
    },
    [currentUserUid, getBookingOwnerId, isSuperAdmin],
  );

  const showAccessDenied = useCallback(() => {
    toast({
      title: "Access denied",
      description:
        "You can only manage bookings for accommodations linked to your account.",
      status: "error",
      duration: 3500,
      isClosable: true,
    });
  }, [toast]);

  useEffect(() => {
    const resolveBookingUsers = async () => {
      const uniqueUserIds = Array.from(
        new Set(
          bookings
            .map((booking) => String(booking.userId || "").trim())
            .filter(
              (bookingUserId) =>
                bookingUserId && bookingUserId.toLowerCase() !== "guest",
            ),
        ),
      );

      const unresolvedUserIds = uniqueUserIds.filter(
        (bookingUserId) => !userNamesById[bookingUserId],
      );

      if (unresolvedUserIds.length === 0) return;

      const resolvedEntries = await Promise.all(
        unresolvedUserIds.map(async (bookingUserId) => {
          try {
            const userSnapshot = await getDoc(doc(db, "users", bookingUserId));

            const profile = userSnapshot.exists()
              ? (userSnapshot.data() as BookingUserProfile)
              : null;

            return [
              bookingUserId,
              getResolvedUserName(profile, bookingUserId),
            ] as const;
          } catch (error) {
            console.error(
              `Failed to resolve booking user ${bookingUserId}:`,
              error,
            );

            return [bookingUserId, "Customer unavailable"] as const;
          }
        }),
      );

      setUserNamesById((current) => ({
        ...current,
        ...Object.fromEntries(resolvedEntries),
      }));
    };

    resolveBookingUsers();
  }, [bookings, userNamesById]);

  const getBookingUserName = useCallback(
    (booking: AccommodationBooking) => {
      const bookingUserId = String(booking.userId || "").trim();

      if (!bookingUserId || bookingUserId.toLowerCase() === "guest") {
        return "Guest customer";
      }

      return userNamesById[bookingUserId] || "Loading customer...";
    },
    [userNamesById],
  );

  // Client-side filter (keeps rest intact)
  const filtered = useMemo(() => {
    let list = [...bookings];

    const text = q.trim().toLowerCase();
    if (text) {
      list = list.filter((b) => {
        const resolvedUserName = getBookingUserName(b);
        const hay =
          `${b._id} ${b.accommodationId} ${b.roomId} ${b.userId || ""} ${resolvedUserName} ${b.orderId || ""}`.toLowerCase();
        return hay.includes(text);
      });
    }

    if (roomId.trim())
      list = list.filter((b) => String(b.roomId) === roomId.trim());
    if (orderId.trim())
      list = list.filter((b) => String(b.orderId || "") === orderId.trim());
    if (userId.trim()) {
      const customerFilter = userId.trim().toLowerCase();

      list = list.filter((b) => {
        const resolvedUserName = getBookingUserName(b).toLowerCase();
        const bookingUserId = String(b.userId || "").toLowerCase();

        return (
          resolvedUserName.includes(customerFilter) ||
          bookingUserId === customerFilter
        );
      });
    }

    list.sort((a, b) => {
      const av = (a as any)[sortKey] || "";
      const bv = (b as any)[sortKey] || "";
      if (av === bv) return 0;
      if (sortDir === "asc") return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    });

    return list;
  }, [
    bookings,
    q,
    roomId,
    orderId,
    userId,
    sortKey,
    sortDir,
    getBookingUserName,
  ]);

  // KPI cards
  const stats = useMemo(() => {
    const confirmed = filtered.filter(
      (b) => b.status === "confirmed" || b.status === "checked_in",
    );
    const cancelled = filtered.filter((b) => b.status === "cancelled");
    const todayCheckIns = confirmed.filter((b) => isToday(b.checkIn));
    const todayCheckOuts = confirmed.filter((b) => isToday(b.checkOut));
    const upcoming = confirmed.filter(
      (b) => b.checkIn > new Date().toISOString().slice(0, 10),
    );

    return {
      todayCheckIns: todayCheckIns.length,
      todayCheckOuts: todayCheckOuts.length,
      upcoming: upcoming.length,
      cancelled: cancelled.length,
      total: filtered.length,
    };
  }, [filtered]);

  const openDetails = (b: AccommodationBooking) => {
    if (!canManageBooking(b)) {
      showAccessDenied();
      return;
    }

    setSelected(b);
    details.onOpen();
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: "Copied", status: "success", duration: 1200 });
    } catch {
      toast({ title: "Copy failed", status: "error", duration: 1500 });
    }
  };

  const cancelBooking = async (b: AccommodationBooking) => {
    if (!canManageBooking(b)) {
      showAccessDenied();
      return;
    }

    if (
      !window.confirm(
        "Cancel this booking? This will free up availability immediately.",
      )
    )
      return;

    setActionBusyId(b._id);
    try {
      await axios.patch(`${BOOKINGS_ENDPOINT}/${b._id}/cancel`, {});
      toast({ title: "Booking cancelled", status: "success", duration: 2000 });
      await fetchBookings();

      if (selected?._id === b._id) {
        setSelected((prev) => (prev ? { ...prev, status: "cancelled" } : prev));
      }
    } catch (e: any) {
      toast({
        title: "Cancel failed",
        description: e?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 4000,
      });
    } finally {
      setActionBusyId(null);
    }
  };

  // ✅ REAL CHECK-IN endpoint
  const checkInBooking = async (b: AccommodationBooking) => {
    if (!canManageBooking(b)) {
      showAccessDenied();
      return;
    }

    setActionBusyId(b._id);
    try {
      const res = await axios.patch(
        `${BOOKINGS_ENDPOINT}/${b._id}/check-in`,
        {},
      );
      toast({ title: "Checked in", status: "success", duration: 1500 });

      // refresh list + drawer
      await fetchBookings();
      if (selected?._id === b._id) setSelected(res.data);
    } catch (e: any) {
      toast({
        title: "Check-in failed",
        description: e?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 4000,
      });
    } finally {
      setActionBusyId(null);
    }
  };

  // ✅ REAL CHECK-OUT endpoint
  const checkOutBooking = async (b: AccommodationBooking) => {
    if (!canManageBooking(b)) {
      showAccessDenied();
      return;
    }

    setActionBusyId(b._id);
    try {
      const res = await axios.patch(
        `${BOOKINGS_ENDPOINT}/${b._id}/check-out`,
        {},
      );
      toast({ title: "Checked out", status: "success", duration: 1500 });

      await fetchBookings();
      if (selected?._id === b._id) setSelected(res.data);
    } catch (e: any) {
      toast({
        title: "Check-out failed",
        description: e?.response?.data?.message || "Please try again.",
        status: "error",
        duration: 4000,
      });
    } finally {
      setActionBusyId(null);
    }
  };

  const resetFilters = () => {
    setStatus("all");
    setFromDate(null);
    setToDate(null);
    setAccommodationId("");
    setRoomId("");
    setOrderId("");
    setUserId("");
    setQ("");
    setSortKey("createdAt");
    setSortDir("desc");
  };

  const canCheckIn = (b: AccommodationBooking) =>
    canManageBooking(b) &&
    b.status !== "cancelled" &&
    b.status !== "checked_in" &&
    b.status !== "checked_out";

  const canCheckOut = (b: AccommodationBooking) =>
    canManageBooking(b) &&
    b.status !== "cancelled" &&
    b.status !== "checked_out";

  return (
    <Box
      borderRadius={cardsBorderRadii}
      p={{ base: 3, sm: 4, md: 6 }}
      bg={theme.surface}
      minH="100vh"
      color={theme.text}
      border="1px solid"
      borderColor={theme.border}
      boxShadow={theme.shadowSm}
    >
      <Flex
        justify="space-between"
        align={{ base: "start", md: "center" }}
        gap={3}
        wrap="wrap"
      >
        <Box>
          <Heading size="lg" color={theme.text}>
            Accommodation Bookings
          </Heading>
          <Text color={theme.textSubtle} fontSize="sm" mt={1}>
            Manage reservations, availability, cancellations, check-ins and
            check-outs.{" "}
            {isSuperAdmin
              ? "Superadmin access includes every accommodation."
              : "Showing bookings for accommodations linked to your account."}
          </Text>
        </Box>

        <Flex gap={2} w={{ base: "100%", sm: "auto" }} flexWrap="wrap">
          <Tooltip label="Refresh">
            <IconButton
              aria-label="Refresh"
              icon={<RepeatIcon />}
              onClick={fetchBookings}
              isLoading={loading || accessLoading}
              variant="outline"
              borderColor={theme.borderStrong}
              color={theme.text}
              _hover={{ bg: theme.hover }}
            />
          </Tooltip>
          <Button
            variant="outline"
            onClick={resetFilters}
            borderColor={theme.borderStrong}
            color={theme.text}
            _hover={{ bg: theme.hover }}
            flex={{ base: 1, sm: "initial" }}
          >
            Reset filters
          </Button>
        </Flex>
      </Flex>

      {/* KPI cards */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mt={5}>
        <StatCard
          label="Today check-ins"
          value={stats.todayCheckIns}
          help={`Out of ${stats.total} shown`}
        />
        <StatCard
          label="Today check-outs"
          value={stats.todayCheckOuts}
          help="Based on filtered list"
        />
        <StatCard
          label="Upcoming"
          value={stats.upcoming}
          help="Confirmed / checked-in stays"
        />
        <StatCard
          label="Cancelled"
          value={stats.cancelled}
          help="Cancelled stays"
        />
      </SimpleGrid>

      {/* Filters */}
      <Box
        mt={6}
        borderWidth="1px"
        borderRadius={cardsBorderRadii}
        p={4}
        borderColor={theme.border}
        bg={theme.surface}
        boxShadow={theme.shadowSm}
      >
        <Flex gap={3} wrap="wrap" align="flex-end">
          <Box minW={{ base: "100%", md: "220px" }}>
            <Text fontSize="xs" color={theme.textSubtle} mb={1}>
              Status
            </Text>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            >
              <option value="all">All</option>
              <option value="confirmed">Confirmed</option>
              <option value="checked_in">Checked-in</option>
              <option value="checked_out">Checked-out</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </Box>

          <Box minW={{ base: "100%", md: "220px" }}>
            <CustomDatePicker
              label="From"
              selected={fromDate}
              onChange={setFromDate}
              placeholder="From date"
            />
          </Box>

          <Box minW={{ base: "100%", md: "220px" }}>
            <CustomDatePicker
              label="To"
              selected={toDate}
              onChange={setToDate}
              placeholder="To date"
            />
          </Box>

          <Box minW={{ base: "100%", md: "260px" }}>
            <Text fontSize="xs" color={theme.textSubtle} mb={1}>
              Accommodation ID
            </Text>
            <Input
              value={accommodationId}
              onChange={(e) => setAccommodationId(e.target.value)}
              placeholder="e.g. 699c..."
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _placeholder={{ color: theme.inputPlaceholder }}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            />
          </Box>

          <Box minW={{ base: "100%", md: "220px" }}>
            <Text fontSize="xs" color={theme.textSubtle} mb={1}>
              Room ID
            </Text>
            <Input
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              placeholder="e.g. e54eayzi"
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _placeholder={{ color: theme.inputPlaceholder }}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            />
          </Box>

          <Box minW={{ base: "100%", md: "220px" }}>
            <Text fontSize="xs" color={theme.textSubtle} mb={1}>
              Order ID
            </Text>
            <Input
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Order reference"
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _placeholder={{ color: theme.inputPlaceholder }}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            />
          </Box>

          <Box minW={{ base: "100%", md: "220px" }}>
            <Text fontSize="xs" color={theme.textSubtle} mb={1}>
              Customer
            </Text>
            <Input
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Customer name"
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _placeholder={{ color: theme.inputPlaceholder }}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            />
          </Box>

          <Box flex="1" minW={{ base: "100%", md: "300px" }}>
            <Text fontSize="xs" color={theme.textSubtle} mb={1}>
              Search
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={theme.textMuted as any} />
              </InputLeftElement>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search customer, booking, room or order..."
                bg={theme.inputBg}
                color={theme.text}
                borderColor={theme.inputBorder}
                _placeholder={{ color: theme.inputPlaceholder }}
                _hover={{ borderColor: theme.inputBorderFocus }}
                _focus={{
                  borderColor: theme.inputBorderFocus,
                  boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                }}
              />
            </InputGroup>
          </Box>

          <Button
            onClick={fetchBookings}
            isLoading={loading || accessLoading}
            minW={{ base: "100%", md: "180px" }}
            color={theme.textOnPrimary}
            bgImage={theme.gradients.primaryButton}
            _hover={{ bgImage: theme.gradients.primaryButtonHover }}
            boxShadow={theme.shadowSm}
          >
            Apply
          </Button>
        </Flex>

        <Stack
          mt={3}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          spacing={3}
        >
          <Flex gap={2} flexWrap="wrap" alignItems="center">
            <Text fontSize="sm" color={theme.textSubtle}>
              Sort:
            </Text>
            <Select
              size="sm"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as any)}
              w={{ base: "100%", sm: "170px" }}
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            >
              <option value="createdAt">Created</option>
              <option value="checkIn">Check-in</option>
              <option value="checkOut">Check-out</option>
            </Select>
            <Select
              size="sm"
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value as any)}
              w={{ base: "100%", sm: "120px" }}
              bg={theme.inputBg}
              color={theme.text}
              borderColor={theme.inputBorder}
              _hover={{ borderColor: theme.inputBorderFocus }}
              _focus={{
                borderColor: theme.inputBorderFocus,
                boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
              }}
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </Select>
          </Flex>

          <Text fontSize="sm" color={theme.textSubtle}>
            Showing <b>{filtered.length}</b> bookings
          </Text>
        </Stack>
      </Box>

      {/* Table */}
      <Box
        mt={6}
        borderWidth="1px"
        borderRadius={cardsBorderRadii}
        overflow="hidden"
        borderColor={theme.border}
        bg={theme.surface}
      >
        <Box
          px={4}
          py={3}
          bg={theme.surfaceAlt}
          borderBottomWidth="1px"
          borderColor={theme.border}
        >
          <HStack justify="space-between" flexWrap="wrap">
            <Text fontWeight="semibold" color={theme.text}>
              Bookings
            </Text>
            {loading && (
              <HStack color={theme.textSubtle}>
                <GlobalSpinner />
                <Text fontSize="sm">Loading…</Text>
              </HStack>
            )}
          </HStack>
        </Box>

        <Box display={{ base: "none", lg: "block" }} overflowX="auto">
          <Table size="sm" minW="1100px">
            <Thead bg={theme.surface}>
              <Tr>
                <Th color={theme.textMuted}>Status</Th>
                <Th color={theme.textMuted}>Check-in</Th>
                <Th color={theme.textMuted}>Check-out</Th>
                <Th color={theme.textMuted}>Nights</Th>
                <Th color={theme.textMuted}>Accommodation</Th>
                <Th color={theme.textMuted}>Room</Th>
                <Th color={theme.textMuted}>Guests</Th>
                <Th color={theme.textMuted}>Customer</Th>
                <Th color={theme.textMuted}>Order</Th>
                <Th color={theme.textMuted}>Created</Th>
                <Th textAlign="right" color={theme.textMuted}>
                  Actions
                </Th>
              </Tr>
            </Thead>

            <Tbody>
              {filtered.map((b) => (
                <Tr
                  key={b._id}
                  _hover={{ bg: theme.hover }}
                  cursor="pointer"
                  onClick={() => openDetails(b)}
                >
                  <Td>
                    <Badge
                      colorScheme={statusColor(b.status)}
                      textTransform="capitalize"
                    >
                      {b.status.replace("_", " ")}
                    </Badge>
                  </Td>
                  <Td color={theme.text}>{b.checkIn}</Td>
                  <Td color={theme.text}>{b.checkOut}</Td>
                  <Td color={theme.text}>
                    {nightsBetween(b.checkIn, b.checkOut)}
                  </Td>

                  <Td>
                    <HStack>
                      <Text noOfLines={1} maxW="150px" color={theme.text}>
                        {b.accommodationId}
                      </Text>
                      <Tooltip label="Copy">
                        <IconButton
                          aria-label="Copy accommodationId"
                          icon={<CopyIcon />}
                          size="xs"
                          variant="ghost"
                          color={theme.textMuted}
                          _hover={{ bg: theme.hover, color: theme.text }}
                          onClick={(e) => {
                            e.stopPropagation();
                            copy(b.accommodationId);
                          }}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>

                  <Td>
                    <HStack>
                      <Text color={theme.text}>{b.roomId}</Text>
                      <Tooltip label="Copy">
                        <IconButton
                          aria-label="Copy roomId"
                          icon={<CopyIcon />}
                          size="xs"
                          variant="ghost"
                          color={theme.textMuted}
                          _hover={{ bg: theme.hover, color: theme.text }}
                          onClick={(e) => {
                            e.stopPropagation();
                            copy(b.roomId);
                          }}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>

                  <Td color={theme.text}>{b.guests ?? 1}</Td>
                  <Td color={theme.text}>{getBookingUserName(b)}</Td>
                  <Td color={theme.text}>{b.orderId || "—"}</Td>
                  <Td color={theme.text}>{formatDateTime(b.createdAt)}</Td>

                  <Td textAlign="right">
                    <HStack justify="flex-end">
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetails(b);
                        }}
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                      >
                        View
                      </Button>

                      <Button
                        size="xs"
                        colorScheme="blue"
                        variant="outline"
                        isLoading={actionBusyId === b._id}
                        isDisabled={!canCheckIn(b)}
                        onClick={(e) => {
                          e.stopPropagation();
                          checkInBooking(b);
                        }}
                      >
                        Check-in
                      </Button>

                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="outline"
                        isLoading={actionBusyId === b._id}
                        isDisabled={!canCheckOut(b)}
                        onClick={(e) => {
                          e.stopPropagation();
                          checkOutBooking(b);
                        }}
                      >
                        Check-out
                      </Button>

                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        isDisabled={b.status === "cancelled"}
                        isLoading={actionBusyId === b._id}
                        onClick={(e) => {
                          e.stopPropagation();
                          cancelBooking(b);
                        }}
                      >
                        Cancel
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}

              {!loading && filtered.length === 0 && (
                <Tr>
                  <Td colSpan={11}>
                    <Box py={10} textAlign="center" color={theme.textSubtle}>
                      No bookings found for the current filters.
                    </Box>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
        <Stack display={{ base: "flex", lg: "none" }} spacing={3} p={3}>
          {filtered.map((b) => (
            <Box
              key={b._id}
              borderWidth="1px"
              borderColor={theme.border}
              borderRadius={cardsBorderRadii}
              p={4}
              bg={theme.surface}
              boxShadow={theme.shadowSm}
              cursor="pointer"
              onClick={() => openDetails(b)}
            >
              <Flex
                justify="space-between"
                align="flex-start"
                gap={3}
                flexWrap="wrap"
              >
                <Box minW={0}>
                  <Text fontWeight="bold" color={theme.text} noOfLines={1}>
                    {getBookingUserName(b)}
                  </Text>
                  <Text fontSize="xs" color={theme.textSubtle} mt={1}>
                    Created {formatDateTime(b.createdAt)}
                  </Text>
                </Box>

                <Badge
                  colorScheme={statusColor(b.status)}
                  textTransform="capitalize"
                  flexShrink={0}
                >
                  {b.status.replace("_", " ")}
                </Badge>
              </Flex>

              <SimpleGrid columns={2} spacing={3} mt={4}>
                <Box>
                  <Text fontSize="xs" color={theme.textSubtle}>
                    Check-in
                  </Text>
                  <Text fontSize="sm" color={theme.text} fontWeight="semibold">
                    {b.checkIn}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={theme.textSubtle}>
                    Check-out
                  </Text>
                  <Text fontSize="sm" color={theme.text} fontWeight="semibold">
                    {b.checkOut}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={theme.textSubtle}>
                    Nights
                  </Text>
                  <Text fontSize="sm" color={theme.text}>
                    {nightsBetween(b.checkIn, b.checkOut)}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={theme.textSubtle}>
                    Guests
                  </Text>
                  <Text fontSize="sm" color={theme.text}>
                    {b.guests ?? 1}
                  </Text>
                </Box>
              </SimpleGrid>

              <Stack spacing={2} mt={4}>
                <Flex justify="space-between" gap={3} align="center">
                  <Box minW={0}>
                    <Text fontSize="xs" color={theme.textSubtle}>
                      Accommodation
                    </Text>
                    <Text fontSize="sm" color={theme.text} noOfLines={1}>
                      {b.accommodationId}
                    </Text>
                  </Box>
                  <IconButton
                    aria-label="Copy accommodationId"
                    icon={<CopyIcon />}
                    size="xs"
                    variant="ghost"
                    color={theme.textMuted}
                    flexShrink={0}
                    _hover={{ bg: theme.hover, color: theme.text }}
                    onClick={(e) => {
                      e.stopPropagation();
                      copy(b.accommodationId);
                    }}
                  />
                </Flex>

                <Flex justify="space-between" gap={3} align="center">
                  <Box minW={0}>
                    <Text fontSize="xs" color={theme.textSubtle}>
                      Room
                    </Text>
                    <Text fontSize="sm" color={theme.text} noOfLines={1}>
                      {b.roomId}
                    </Text>
                  </Box>
                  <IconButton
                    aria-label="Copy roomId"
                    icon={<CopyIcon />}
                    size="xs"
                    variant="ghost"
                    color={theme.textMuted}
                    flexShrink={0}
                    _hover={{ bg: theme.hover, color: theme.text }}
                    onClick={(e) => {
                      e.stopPropagation();
                      copy(b.roomId);
                    }}
                  />
                </Flex>

                <Box>
                  <Text fontSize="xs" color={theme.textSubtle}>
                    Order
                  </Text>
                  <Text fontSize="sm" color={theme.text} wordBreak="break-word">
                    {b.orderId || "—"}
                  </Text>
                </Box>
              </Stack>

              <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={2} mt={4}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDetails(b);
                  }}
                  borderColor={theme.borderStrong}
                  color={theme.text}
                  _hover={{ bg: theme.hover }}
                >
                  View
                </Button>

                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  isLoading={actionBusyId === b._id}
                  isDisabled={!canCheckIn(b)}
                  onClick={(e) => {
                    e.stopPropagation();
                    checkInBooking(b);
                  }}
                >
                  Check-in
                </Button>

                <Button
                  size="sm"
                  colorScheme="purple"
                  variant="outline"
                  isLoading={actionBusyId === b._id}
                  isDisabled={!canCheckOut(b)}
                  onClick={(e) => {
                    e.stopPropagation();
                    checkOutBooking(b);
                  }}
                >
                  Check-out
                </Button>

                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  isDisabled={b.status === "cancelled"}
                  isLoading={actionBusyId === b._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    cancelBooking(b);
                  }}
                >
                  Cancel
                </Button>
              </SimpleGrid>
            </Box>
          ))}

          {!loading && filtered.length === 0 && (
            <Box py={10} textAlign="center" color={theme.textSubtle}>
              No bookings found for the current filters.
            </Box>
          )}
        </Stack>
      </Box>

      {/* Details Drawer */}
      <Drawer
        isOpen={details.isOpen}
        placement="right"
        onClose={details.onClose}
        size="md"
      >
        <DrawerOverlay bg={theme.overlay} />
        <DrawerContent
          bg={theme.surface}
          color={theme.text}
          w={{ base: "100vw", md: "520px" }}
          maxW={{ base: "100vw", md: "520px" }}
        >
          <DrawerCloseButton color={theme.text} />
          <DrawerHeader borderBottomWidth="1px" borderColor={theme.border}>
            Booking details
          </DrawerHeader>

          <DrawerBody>
            {!selected ? (
              <Text color={theme.textSubtle}>No booking selected.</Text>
            ) : (
              <Stack spacing={4}>
                <Box
                  borderWidth="1px"
                  borderRadius={cardsBorderRadii}
                  p={3}
                  borderColor={theme.border}
                  bg={theme.surface}
                >
                  <Stack
                    direction={{ base: "column", sm: "row" }}
                    justify="space-between"
                    align={{ base: "stretch", sm: "center" }}
                    spacing={3}
                  >
                    <Badge
                      colorScheme={statusColor(selected.status)}
                      textTransform="capitalize"
                    >
                      {selected.status.replace("_", " ")}
                    </Badge>

                    <Flex gap={2} flexWrap="wrap">
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => copy(selected._id)}
                        borderColor={theme.borderStrong}
                        color={theme.text}
                        _hover={{ bg: theme.hover }}
                      >
                        Copy Booking ID
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        isDisabled={selected.status === "cancelled"}
                        isLoading={actionBusyId === selected._id}
                        onClick={() => cancelBooking(selected)}
                      >
                        Cancel booking
                      </Button>
                    </Flex>
                  </Stack>

                  <Stack mt={3} spacing={1} fontSize="sm">
                    <Text color={theme.text}>
                      <b>Booking ID:</b> {selected._id}
                    </Text>
                    <Text color={theme.text}>
                      <b>Accommodation ID:</b> {selected.accommodationId}
                    </Text>
                    <Text color={theme.text}>
                      <b>Room ID:</b> {selected.roomId}
                    </Text>
                    <Text color={theme.text}>
                      <b>Customer:</b> {getBookingUserName(selected)}
                    </Text>
                    <Text color={theme.text}>
                      <b>Order ID:</b> {selected.orderId || "—"}
                    </Text>
                    <Text color={theme.text}>
                      <b>Guests:</b> {selected.guests ?? 1}
                    </Text>
                    <Text color={theme.text}>
                      <b>Rooms booked:</b> {selected.roomsBooked ?? 1}
                    </Text>
                    <Text color={theme.text}>
                      <b>Check-in:</b> {selected.checkIn}
                    </Text>
                    <Text color={theme.text}>
                      <b>Check-out:</b> {selected.checkOut}
                    </Text>
                    <Text color={theme.text}>
                      <b>Nights:</b>{" "}
                      {nightsBetween(selected.checkIn, selected.checkOut)}
                    </Text>
                    <Text color={theme.text}>
                      <b>Created:</b> {formatDateTime(selected.createdAt)}
                    </Text>
                    <Text color={theme.text}>
                      <b>Updated:</b> {formatDateTime(selected.updatedAt)}
                    </Text>
                    {selected.checkedInAt && (
                      <Text color={theme.text}>
                        <b>Checked-in at:</b>{" "}
                        {formatDateTime(selected.checkedInAt)}
                      </Text>
                    )}
                    {selected.checkedOutAt && (
                      <Text color={theme.text}>
                        <b>Checked-out at:</b>{" "}
                        {formatDateTime(selected.checkedOutAt)}
                      </Text>
                    )}
                  </Stack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderRadius={cardsBorderRadii}
                  p={3}
                  borderColor={theme.border}
                  bg={theme.surfaceAlt}
                >
                  <Text fontWeight="bold" mb={2} color={theme.text}>
                    Quick actions
                  </Text>
                  <Stack spacing={2}>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      isLoading={actionBusyId === selected._id}
                      isDisabled={!canCheckIn(selected)}
                      onClick={() => checkInBooking(selected)}
                    >
                      Mark checked-in
                    </Button>

                    <Button
                      variant="outline"
                      colorScheme="purple"
                      isLoading={actionBusyId === selected._id}
                      isDisabled={!canCheckOut(selected)}
                      onClick={() => checkOutBooking(selected)}
                    >
                      Mark checked-out
                    </Button>

                    <Text fontSize="xs" color={theme.textSubtle}>
                      These actions call backend endpoints. Ensure your schema
                      supports checked_in / checked_out.
                    </Text>
                  </Stack>
                </Box>

                <Box
                  borderWidth="1px"
                  borderRadius={cardsBorderRadii}
                  p={3}
                  bg={theme.surfaceAlt}
                  borderColor={theme.border}
                >
                  <Text fontWeight="bold" mb={1} color={theme.text}>
                    Admin tips
                  </Text>
                  <Text fontSize="sm" color={theme.textSubtle}>
                    Cancelling frees availability immediately because
                    availability subtracts only active bookings that overlap the
                    date range.
                  </Text>
                </Box>
              </Stack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

function StatCard({
  label,
  value,
  help,
}: {
  label: string;
  value: number;
  help?: string;
}) {
  const { theme } = useCustomTheme();

  return (
    <Box
      borderWidth="1px"
      borderRadius={cardsBorderRadii}
      p={4}
      borderColor={theme.border}
      bg={theme.surface}
      boxShadow={theme.shadowSm}
    >
      <Stat>
        <StatLabel color={theme.textSubtle}>{label}</StatLabel>
        <StatNumber color={theme.text}>{value}</StatNumber>
        <StatHelpText color={theme.textMuted}>{help || ""}</StatHelpText>
      </Stat>
    </Box>
  );
}
